import { useState, useCallback, useEffect, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import { FileText, RefreshCw, X, Brain, SearchCode, CheckCircle, Euro, MapPin, Calendar, Briefcase, Gavel, Building2, Check, CircleDashed, FileSearch, Users, Cpu } from "lucide-react"
import { DashboardLayout } from "@/components/common/DashboardLayout"
import { DataTable, ColumnDef } from "@/components/common/DataTable"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { FilterSheet } from "@/components/ui/filter-sheet"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { api } from "@/lib/apiService"
import { useLocaleStore } from "@/stores/localeStore"
import { formatCurrency, formatDate, formatDateRelative, getRelevanceBgColor } from "@/lib/utils"
import type { Tender, TenderListResponse, AiSearchResponse, FilterOptionsResponse } from "@/types/tender.types"
import type { FilterGroupDefinition, FilterSelections } from "@/types/filters"

interface TenderFilters {
	search: string
	status: string[]
	contractType: string[]
	procedure: string[]
	minBudget: string
	maxBudget: string
	location: string
	province: string[]
	state: string[]
	onlyRelevant: boolean
	deadlineFrom: string
	deadlineTo: string
	publishedFrom: string
	publishedTo: string
	pipelineStatus: string[]
}

const DEFAULT_FILTERS: TenderFilters = {
	search: "",
	status: [],
	contractType: [],
	procedure: [],
	minBudget: "",
	maxBudget: "",
	location: "",
	province: [],
	state: [],
	onlyRelevant: false,
	deadlineFrom: "",
	deadlineTo: "",
	publishedFrom: "",
	publishedTo: "",
	pipelineStatus: [],
}

function countActiveFilters(filters: TenderFilters): number {
	let count = 0
	if (filters.status.length > 0) count++
	if (filters.contractType.length > 0) count++
	if (filters.procedure.length > 0) count++
	if (filters.minBudget || filters.maxBudget) count++
	if (filters.location) count++
	if (filters.province.length > 0) count++
	if (filters.state.length > 0) count++
	if (filters.onlyRelevant) count++
	if (filters.deadlineFrom || filters.deadlineTo) count++
	if (filters.publishedFrom || filters.publishedTo) count++
	if (filters.pipelineStatus.length > 0) count++
	return count
}

function filtersToSelections(filters: TenderFilters): FilterSelections {
	return {
		status: filters.status,
		contractType: filters.contractType,
		procedure: filters.procedure,
		budget: [filters.minBudget, filters.maxBudget],
		location: [filters.location],
		province: filters.province,
		state: filters.state,
		deadline: [filters.deadlineFrom, filters.deadlineTo],
		publishedDate: [filters.publishedFrom, filters.publishedTo],
		pipelineStatus: filters.pipelineStatus,
	}
}

function selectionsToFilters(selections: FilterSelections, current: TenderFilters): TenderFilters {
	return {
		...current,
		status: selections.status ?? [],
		contractType: selections.contractType ?? [],
		procedure: selections.procedure ?? [],
		minBudget: selections.budget?.[0] ?? "",
		maxBudget: selections.budget?.[1] ?? "",
		location: selections.location?.[0] ?? "",
		province: selections.province ?? [],
		state: selections.state ?? [],
		deadlineFrom: selections.deadline?.[0] ?? "",
		deadlineTo: selections.deadline?.[1] ?? "",
		publishedFrom: selections.publishedDate?.[0] ?? "",
		publishedTo: selections.publishedDate?.[1] ?? "",
		pipelineStatus: selections.pipelineStatus ?? [],
	}
}

function RelevanceBadge({ score, reason }: { score?: number | null; reason?: string | null }) {
	if (score == null) {
		return <span className="text-xs text-muted-foreground">—</span>
	}

	const badge = (
		<span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${getRelevanceBgColor(score)}`}>
			<Brain className="h-3 w-3" />
			{score}%
		</span>
	)

	if (!reason) return badge

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>{badge}</TooltipTrigger>
				<TooltipContent side="left" className="max-w-xs text-xs">
					{reason}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}

function StatusBadge({ status }: { status: string }) {
	const colorMap: Record<string, string> = {
		Publicada: "bg-green-50 text-green-700 border-green-200",
		Adjudicada: "bg-blue-50 text-blue-700 border-blue-200",
		Resuelta: "bg-gray-50 text-gray-600 border-gray-200",
		Anulada: "bg-red-50 text-red-600 border-red-200",
	}
	const cls = colorMap[status] || "bg-gray-50 text-gray-600 border-gray-200"
	return (
		<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
			{status}
		</span>
	)
}

function PipelineProgress({ tender, t }: { tender: Tender; t: ReturnType<typeof useTranslations> }) {
	const steps = [
		{ done: tender.detail_fetched, icon: FileSearch, label: t("tenders.pipeline.detail") },
		{ done: tender.ai_relevance_score != null, icon: Cpu, label: t("tenders.pipeline.aiScore") },
		{ done: tender.documents_scraped, icon: FileText, label: t("tenders.pipeline.documents") },
		{ done: tender.contacts_extracted, icon: Users, label: t("tenders.pipeline.contacts") },
	]

	const completed = steps.filter((s) => s.done).length
	const allDone = completed === steps.length

	if (allDone) {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="inline-flex items-center gap-1 text-xs text-green-600">
							<CheckCircle className="h-3.5 w-3.5" />
							{completed}/{steps.length}
						</span>
					</TooltipTrigger>
					<TooltipContent side="left" className="text-xs">
						{t("tenders.pipeline.complete")}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		)
	}

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<span className="inline-flex items-center gap-1 text-xs text-amber-600">
						<CircleDashed className="h-3.5 w-3.5" />
						{completed}/{steps.length}
					</span>
				</TooltipTrigger>
				<TooltipContent side="left" className="text-xs">
					<div className="space-y-1">
						{steps.map((step, i) => (
							<div key={i} className={`flex items-center gap-1.5 ${step.done ? "text-green-600" : "text-muted-foreground"}`}>
								{step.done ? <Check className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />}
								{step.label}
							</div>
						))}
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}

type SyncStatus = { id: string; status: "running" | "completed" | "failed"; tenders_fetched: number; tenders_created: number; tenders_updated: number; started_at: string; finished_at?: string }

const MAX_TENDERS = 1000

function SyncProgressWidget({ syncStatus, t }: { syncStatus: SyncStatus; t: ReturnType<typeof useTranslations> }) {
	const [elapsed, setElapsed] = useState("")

	useEffect(() => {
		const update = () => {
			const start = new Date(syncStatus.started_at).getTime()
			const diff = Math.max(0, Math.floor((Date.now() - start) / 1000))
			const m = Math.floor(diff / 60)
			const s = diff % 60
			setElapsed(m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`)
		}
		update()
		const id = setInterval(update, 1000)
		return () => clearInterval(id)
	}, [syncStatus.started_at])

	const pct = syncStatus.tenders_fetched > 0
		? Math.min(Math.round((syncStatus.tenders_fetched / MAX_TENDERS) * 100), 95)
		: 0
	const hasFetched = syncStatus.tenders_fetched > 0

	return (
		<div className="flex items-center gap-2.5 h-9 px-3 rounded-md border border-border bg-background text-sm">
			<RefreshCw className="h-3.5 w-3.5 animate-spin shrink-0 text-[#2D8A5E]" />
			<div className="flex flex-col gap-1 min-w-0">
				<div className="flex items-center gap-1.5 text-xs leading-none">
					<span className="font-medium text-foreground">
						{hasFetched ? `${pct}%` : t("tenders.syncing")}
					</span>
					{hasFetched && (
						<span className="text-muted-foreground tabular-nums">
							· {syncStatus.tenders_fetched.toLocaleString()} {t("tenders.syncFetched")}
						</span>
					)}
					<span className="text-muted-foreground/60 tabular-nums">{elapsed}</span>
				</div>
				<div className="h-1 w-32 bg-muted rounded-full overflow-hidden">
					<div
						className={`h-full bg-[#2D8A5E] rounded-full transition-all duration-700 ${!hasFetched ? "animate-indeterminate w-1/3" : ""}`}
						style={hasFetched ? { width: `${pct}%` } : undefined}
					/>
				</div>
			</div>
		</div>
	)
}

export default function HomePage() {
	const t = useTranslations()
	const navigate = useNavigate()
	const { locale } = useLocaleStore()

	const [tenders, setTenders] = useState<Tender[]>([])
	const [total, setTotal] = useState(0)
	const [page, setPage] = useState(1)
	const [pages, setPages] = useState(1)
	const [isLoading, setIsLoading] = useState(true)
	const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null)
	const [syncDone, setSyncDone] = useState<SyncStatus | null>(null)
	const [lastSyncDate, setLastSyncDate] = useState<string | null>(null)
	const syncPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const [isAiSearching, setIsAiSearching] = useState(false)
	const [isAiMode, setIsAiMode] = useState(false)

	const [filters, setFilters] = useState<TenderFilters>(DEFAULT_FILTERS)
	const [filterOpen, setFilterOpen] = useState(false)
	const [sortBy, setSortBy] = useState("published_at")
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")

	const [aiQuery, setAiQuery] = useState("")
	const [filterOptions, setFilterOptions] = useState<FilterOptionsResponse["data"] | null>(null)

	useEffect(() => {
		api.get<FilterOptionsResponse>("/tenders/filter-options")
			.then((res) => setFilterOptions(res.data))
			.catch(() => {})
	}, [])

	const filterSelections = useMemo(() => filtersToSelections(filters), [filters])

	const fetchTenders = useCallback(async (currentFilters: TenderFilters, currentPage: number) => {
		setIsLoading(true)
		setIsAiMode(false)
		try {
			const params = new URLSearchParams()
			params.set("page", String(currentPage))
			params.set("limit", "20")
			params.set("sortBy", sortBy)
			params.set("sortOrder", sortOrder)

			if (currentFilters.search) params.set("search", currentFilters.search)
			if (currentFilters.location) params.set("location", currentFilters.location)
			if (currentFilters.minBudget) params.set("minBudget", currentFilters.minBudget)
			if (currentFilters.maxBudget) params.set("maxBudget", currentFilters.maxBudget)
			if (currentFilters.onlyRelevant) params.set("onlyRelevant", "true")
			if (currentFilters.deadlineFrom) params.set("deadlineFrom", currentFilters.deadlineFrom)
			if (currentFilters.deadlineTo) params.set("deadlineTo", currentFilters.deadlineTo)
			if (currentFilters.publishedFrom) params.set("publishedFrom", currentFilters.publishedFrom)
			if (currentFilters.publishedTo) params.set("publishedTo", currentFilters.publishedTo)
			currentFilters.status.forEach((s) => params.append("status", s))
			currentFilters.contractType.forEach((s) => params.append("contractType", s))
			currentFilters.procedure.forEach((s) => params.append("procedure", s))
			currentFilters.province.forEach((s) => params.append("province", s))
			currentFilters.state.forEach((s) => params.append("state", s))
			currentFilters.pipelineStatus.forEach((s) => params.append("pipelineStatus", s))

			const res = await api.get<TenderListResponse>(`/tenders?${params.toString()}`)
			setTenders(res.data)
			setTotal(res.total)
			setPages(res.pages)
		} catch {
			toast.error(t("tenders.loadError"))
		} finally {
			setIsLoading(false)
		}
	}, [sortBy, sortOrder])

	useEffect(() => {
		fetchTenders(filters, page)
	}, [fetchTenders, filters, page])

	const handleAiSearch = async () => {
		if (!aiQuery.trim() || aiQuery.length < 3) return
		setIsAiSearching(true)
		try {
			const res = await api.post<AiSearchResponse>("/tenders/ai-search", { query: aiQuery, limit: 30 })
			setTenders(res.data)
			setTotal(res.data.length)
			setPages(1)
			setIsAiMode(true)
			if (res.extractedFilters && Object.keys(res.extractedFilters).length > 0) {
				toast.info(`IA detectó: ${JSON.stringify(res.extractedFilters)}`, { duration: 4000 })
			}
		} catch {
			toast.error(t("tenders.aiSearchError"))
		} finally {
			setIsAiSearching(false)
		}
	}

	const handleApplyFilters = (selections: FilterSelections) => {
		setFilters(selectionsToFilters(selections, filters))
		setPage(1)
	}

	const handleClearFilters = () => {
		setFilters(DEFAULT_FILTERS)
		setPage(1)
	}

	// Use refs to track sync state inside interval callbacks without stale closures
	const syncStatusRef = useRef<SyncStatus | null>(null)
	const fetchTendersRef = useRef(fetchTenders)
	const filtersRef = useRef(filters)
	const pageRef = useRef(page)
	useEffect(() => { fetchTendersRef.current = fetchTenders }, [fetchTenders])
	useEffect(() => { filtersRef.current = filters }, [filters])
	useEffect(() => { pageRef.current = page }, [page])

	const schedulePoll = useCallback((intervalMs: number) => {
		if (syncPollRef.current) clearInterval(syncPollRef.current)
		syncPollRef.current = setInterval(async () => {
			try {
				const res = await api.get<{ success: boolean; data: SyncStatus[] }>("/sync/status")
				const allLogs = res.data ?? []
				const latest = allLogs[0] ?? null
				const wasRunning = syncStatusRef.current?.status === "running"
				const isRunning = latest?.status === "running"

				// Track last completed sync date
				const lastCompleted = allLogs.find((l) => l.status === "completed" && l.finished_at)
				if (lastCompleted?.finished_at) setLastSyncDate(lastCompleted.finished_at)

				if (isRunning) {
					syncStatusRef.current = latest
					setSyncStatus(latest)
					setSyncDone(null)
					// Ensure we're on fast poll
					if (intervalMs !== 3000) schedulePoll(3000)
				} else {
					if (wasRunning) {
						// Just finished — show result and refresh table
						syncStatusRef.current = null
						setSyncStatus(null)
						if (latest?.status === "completed") {
							setSyncDone(latest)
							if (latest.finished_at) setLastSyncDate(latest.finished_at)
							fetchTendersRef.current(filtersRef.current, pageRef.current)
							setTimeout(() => setSyncDone(null), 8000)
						}
					}
					// Switch to slow poll
					if (intervalMs !== 30000) schedulePoll(30000)
				}
			} catch {}
		}, intervalMs)
	}, [])

	useEffect(() => {
		const checkInitial = async () => {
			try {
				const res = await api.get<{ success: boolean; data: SyncStatus[] }>("/sync/status")
				const allLogs = res.data ?? []
				const latest = allLogs[0] ?? null

				// Track last completed sync date
				const lastCompleted = allLogs.find((l) => l.status === "completed" && l.finished_at)
				if (lastCompleted?.finished_at) setLastSyncDate(lastCompleted.finished_at)

				if (latest?.status === "running") {
					syncStatusRef.current = latest
					setSyncStatus(latest)
					schedulePoll(3000)
				} else {
					schedulePoll(30000)
				}
			} catch {
				schedulePoll(30000)
			}
		}
		checkInitial()
		return () => { if (syncPollRef.current) clearInterval(syncPollRef.current) }
	}, [schedulePoll])

	const handleSync = async () => {
		if (syncStatusRef.current?.status === "running") return
		try {
			// Optimistic: show running state immediately so user gets instant feedback
			const optimistic: SyncStatus = {
				id: "", status: "running", tenders_fetched: 0,
				tenders_created: 0, tenders_updated: 0, started_at: new Date().toISOString(),
			}
			syncStatusRef.current = optimistic
			setSyncStatus(optimistic)
			setSyncDone(null)

			await api.post("/sync/trigger", {})
			// Immediate first poll to get real logId
			try {
				const res = await api.get<{ success: boolean; data: SyncStatus[] }>("/sync/status")
				const latest = res.data?.[0] ?? null
				if (latest?.status === "running") {
					syncStatusRef.current = latest
					setSyncStatus(latest)
				}
			} catch {}
			schedulePoll(3000)
		} catch {
			syncStatusRef.current = null
			setSyncStatus(null)
			toast.error(t("tenders.syncError"))
		}
	}

	const handleSort = (col: string, order: "asc" | "desc") => {
		setSortBy(col)
		setSortOrder(order)
		setPage(1)
	}

	const activeFilterCount = countActiveFilters(filters)

	const filterGroups: FilterGroupDefinition[] = useMemo(() => [
		{
			key: "status",
			label: t("tenders.filterPanel.status"),
			icon: CheckCircle,
			type: "multi-select" as const,
			options: (filterOptions?.statuses || []).map((s) => ({ value: s, label: s })),
		},
		{
			key: "contractType",
			label: t("tenders.filterPanel.contractType"),
			icon: Briefcase,
			type: "multi-select" as const,
			options: (filterOptions?.contractTypes || []).map((s) => ({ value: s, label: s })),
		},
		{
			key: "procedure",
			label: t("tenders.filterPanel.procedure"),
			icon: Gavel,
			type: "multi-select" as const,
			options: (filterOptions?.procedures || []).map((s) => ({ value: s, label: s })),
		},
		{
			key: "budget",
			label: t("tenders.filterPanel.budget"),
			icon: Euro,
			type: "number-range" as const,
			options: [],
			numberRangeLabels: {
				min: t("tenders.filterPanel.minBudget"),
				max: t("tenders.filterPanel.maxBudget"),
				minPlaceholder: "0",
				maxPlaceholder: "\u221E",
			},
		},
		{
			key: "location",
			label: t("tenders.filterPanel.location"),
			icon: MapPin,
			type: "text" as const,
			options: [],
			placeholder: t("tenders.filterPanel.locationPlaceholder"),
		},
		{
			key: "deadline",
			label: t("tenders.filterPanel.deadline"),
			icon: Calendar,
			type: "date-range" as const,
			options: [],
		},
		{
			key: "publishedDate",
			label: t("tenders.filterPanel.publishedDate"),
			icon: Calendar,
			type: "date-range" as const,
			options: [],
		},
		{
			key: "province",
			label: t("tenders.filterPanel.province"),
			icon: Building2,
			type: "multi-select" as const,
			options: (filterOptions?.provinces || []).map((s) => ({ value: s, label: s })),
		},
		{
			key: "state",
			label: t("tenders.filterPanel.state"),
			icon: MapPin,
			type: "multi-select" as const,
			options: (filterOptions?.states || []).map((s) => ({ value: s, label: s })),
		},
		{
			key: "pipelineStatus",
			label: t("tenders.filterPanel.pipelineStatus"),
			icon: Cpu,
			type: "multi-select" as const,
			options: [
				{ value: "pending_detail", label: t("tenders.pipeline.pendingDetail") },
				{ value: "pending_ai", label: t("tenders.pipeline.pendingAi") },
				{ value: "pending_documents", label: t("tenders.pipeline.pendingDocuments") },
				{ value: "pending_contacts", label: t("tenders.pipeline.pendingContacts") },
				{ value: "complete", label: t("tenders.pipeline.complete") },
			],
		},
	], [t, filterOptions])

	const columns: ColumnDef<Tender>[] = [
		{
			id: "name",
			header: t("tenders.columns.name"),
			sortable: true,
			cell: (row) => (
				<div className="max-w-md">
					<p className="font-medium text-sm text-gray-900 line-clamp-2 leading-snug">{row.name}</p>
					<p className="text-xs text-muted-foreground mt-0.5">{row.expedient}</p>
					{row.contracting_org_name && (
						<p className="text-xs text-muted-foreground truncate max-w-xs">{row.contracting_org_name}</p>
					)}
				</div>
			),
		},
		{
			id: "budget_no_taxes",
			header: t("tenders.columns.budget"),
			sortable: true,
			className: "text-right whitespace-nowrap",
			headerClassName: "text-right",
			cell: (row) => (
				<span className="text-sm font-mono font-medium">{formatCurrency(row.budget_no_taxes)}</span>
			),
		},
		{
			id: "status",
			header: t("tenders.columns.status"),
			cell: (row) => <StatusBadge status={row.status} />,
		},
		{
			id: "published_at",
			header: t("tenders.columns.publishedAt"),
			sortable: true,
			cell: (row) => (
				<div className="text-sm whitespace-nowrap">
					<p>{formatDate(row.published_at)}</p>
				</div>
			),
		},
		{
			id: "submission_deadline",
			header: t("tenders.columns.deadline"),
			sortable: true,
			cell: (row) => (
				<div className="text-sm whitespace-nowrap">
					<p>{formatDate(row.submission_deadline)}</p>
					<p className="text-xs text-muted-foreground">{formatDateRelative(row.submission_deadline, locale)}</p>
				</div>
			),
		},
		{
			id: "contract_type",
			header: t("tenders.columns.contractType"),
			cell: (row) => (
				<span className="text-xs text-muted-foreground">{row.contract_type || "—"}</span>
			),
		},
		{
			id: "location",
			header: t("tenders.columns.location"),
			cell: (row) => (
				<span className="text-sm text-muted-foreground truncate max-w-[140px] block">{row.location || "—"}</span>
			),
		},
		{
			id: "ai_relevance_score",
			header: t("tenders.columns.score"),
			sortable: true,
			cell: (row) => <RelevanceBadge score={row.ai_relevance_score} reason={row.ai_relevance_reason} />,
		},
		{
			id: "pipeline",
			header: t("tenders.columns.pipeline"),
			cell: (row) => <PipelineProgress tender={row} t={t} />,
		},
	]

	return (
		<DashboardLayout>
			{/* AI Search bar */}
			<div className="bg-gradient-to-r from-[#2d5c3f]/5 via-[#3d7a4f]/5 to-[#4a9960]/5 border border-[#2d5c3f]/10 rounded-xl p-4">
				<div className="flex items-center gap-2 mb-3">
					<SearchCode className="h-4 w-4 text-[#2D8A5E]" />
					<span className="text-sm font-semibold text-[#2d5c3f]">{t("tenders.aiSearch")}</span>
				</div>
				<div className="flex gap-2">
					<Input
						value={aiQuery}
						onChange={(e) => setAiQuery(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleAiSearch()}
						placeholder={t("tenders.aiSearchPlaceholder")}
						className="flex-1 bg-white border-[#2d5c3f]/20 focus:border-[#2D8A5E] focus:ring-[#2D8A5E]"
					/>
					<Button
						onClick={handleAiSearch}
						disabled={isAiSearching || aiQuery.length < 3}
						className="bg-[#2D8A5E] hover:bg-[#2d5c3f] text-white whitespace-nowrap"
					>
						{isAiSearching ? (
							<><RefreshCw className="h-4 w-4 mr-2 animate-spin" />{t("tenders.aiSearching")}</>
						) : (
							<><Brain className="h-4 w-4 mr-2" />{t("tenders.aiSearchButton")}</>
						)}
					</Button>
					{isAiMode && (
						<Button variant="outline" size="icon" onClick={() => { setAiQuery(""); setPage(1); fetchTenders(filters, 1) }}>
							<X className="h-4 w-4" />
						</Button>
					)}
				</div>

				{/* Optimtech-relevant filter checkbox */}
				<div className="mt-3 flex items-center gap-2">
					<Checkbox
						id="onlyRelevant"
						checked={filters.onlyRelevant}
						onCheckedChange={(checked) => {
							setFilters((prev) => ({ ...prev, onlyRelevant: !!checked }))
							setPage(1)
						}}
					/>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<label htmlFor="onlyRelevant" className="text-sm font-medium text-[#2d5c3f] cursor-pointer select-none flex items-center gap-1.5">
									<Brain className="h-3.5 w-3.5" />
									{t("tenders.onlyRelevant")}
								</label>
							</TooltipTrigger>
							<TooltipContent side="right" className="max-w-xs text-xs">
								{t("tenders.onlyRelevantTooltip")}
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
			</div>

			{/* Table */}
			<DataTable
				data={tenders}
				columns={columns}
				isLoading={isLoading}
				headerExtra={
					syncStatus?.status === "running" ? (
						<SyncProgressWidget syncStatus={syncStatus} t={t} />
					) : syncDone ? (
						<div className="flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#2D8A5E]/30 bg-[#2D8A5E]/5 text-xs text-[#2D8A5E]">
							<Check className="h-3.5 w-3.5 shrink-0" />
							<span>{syncDone.tenders_fetched.toLocaleString()} {t("tenders.syncFetched")} · {syncDone.tenders_created} {t("tenders.syncNew")}, {syncDone.tenders_updated} {t("tenders.syncUpdated")}</span>
						</div>
					) : (
						<div className="flex items-center gap-2">
							{lastSyncDate && (
								<span className="text-xs text-muted-foreground/60">
									{t("tenders.lastSync", { date: formatDate(lastSyncDate) + " " + new Date(lastSyncDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}
								</span>
							)}
							<Button variant="outline" size="sm" onClick={handleSync}>
								<RefreshCw className="h-4 w-4 mr-2" />
								{t("tenders.sync")}
							</Button>
						</div>
					)
				}
				title={
					isAiMode
						? `${t("tenders.title")} — ${t("tenders.aiSearchResults")}`
						: `${t("tenders.title")} ${!isLoading ? `(${total.toLocaleString()})` : ""}`
				}
				titleIcon={<FileText className="h-5 w-5 text-[#2D8A5E]" />}
				searchPlaceholder={t("tenders.search")}
				onSearchChange={(val) => {
					setFilters((prev) => ({ ...prev, search: val }))
					setPage(1)
				}}
				searchValue={filters.search}
				emptyMessage={t("tenders.empty")}
				emptyIcon={<FileText className="h-12 w-12 text-muted-foreground/30" />}
				onRowClick={(row) => navigate(`/tenders/${row.id}`)}
				sortBy={sortBy}
				sortOrder={sortOrder}
				onSort={handleSort}
				pagination={
					pages > 1
						? { page, limit: 20, total, pages, onPageChange: setPage }
						: undefined
				}
				filterConfig={{
					activeCount: activeFilterCount,
					onOpen: () => setFilterOpen(true),
					label: t("tenders.filters"),
				}}
				getRowId={(row) => row.id}
			/>

			{/* Filter Sheet */}
			<FilterSheet
				open={filterOpen}
				onOpenChange={setFilterOpen}
				title={t("tenders.filterPanel.title")}
				groups={filterGroups}
				selections={filterSelections}
				onApply={handleApplyFilters}
				onClearAll={handleClearFilters}
				applyLabel={t("tenders.filterPanel.apply")}
				clearAllLabel={t("tenders.filterPanel.clear")}
				dateFromLabel={t("tenders.filterPanel.from")}
				dateToLabel={t("tenders.filterPanel.to")}
				selectPlaceholder={t("common.selectPlaceholder")}
				searchPlaceholder={t("common.searchPlaceholder")}
				noResultsLabel={t("common.noResults")}
			/>
		</DashboardLayout>
	)
}
